import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { newsApi } from '@/services/api'
import { isAuthenticated } from '@/lib/utils'
import { toast } from 'sonner'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle2, XCircle, AlertCircle, Info } from 'lucide-react'

export function DonationSection({ article }) {
  const amounts = [0, 0.25, 0.5, 1.0]
  const [customAmount, setCustomAmount] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)
  const navigate = useNavigate()
  const [modal, setModal] = useState({
    open: false,
    success: null,
    message: '',
    showUpdate: false,
    newAmount: null,
    overFive: false,
    amount: null,
    isCancellation: false,
  })
  const [existingDonation, setExistingDonation] = useState(null)

  const feeder = article?.feeder || {}

  // Check for existing donation
  useEffect(() => {
    if (!isAuthenticated() || !article) return

    newsApi.getDonations({
      feeder: feeder.alias,
      article: article.alias,
    })
      .then((res) => {
        if (res.data && res.data.length > 0) {
          setExistingDonation(res.data[0])
        }
      })
      .catch((err) => {
        console.error('Donation fetch failed:', err)
      })
  }, [feeder.alias, article?.alias])

  // Close modal on Escape (treats as Back/Cancel without action)
  useEffect(() => {
    if (!modal.open) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setModal((prev) => ({ ...prev, open: false }))
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [modal.open])

  const handleDonate = async (amount) => {
    if (!isAuthenticated()) {
      localStorage.setItem('ldirect', window.location.pathname)
      navigate('/login')
      return
    }

    if (amount > 5) {
      setModal({
        open: true,
        success: null,
        message: '',
        showUpdate: false,
        newAmount: null,
        overFive: true,
        amount: amount,
      })
      return
    }

    if (existingDonation) {
      const message = amount === 0
        ? `Cancel your donation of $${existingDonation.amount}?`
        : `You already donated $${existingDonation.amount}. Update to $${amount}?`
      setModal({
        open: true,
        success: null,
        message,
        showUpdate: true,
        newAmount: amount,
        overFive: false,
        isCancellation: amount === 0,
      })
      return
    }

    if (amount === 0) {
      toast.info('No donation to cancel')
      return
    }

    return donate(amount)
  }

  const donate = async (amount) => {
    try {
      let res
      if (existingDonation) {
        if (amount === 0) {
          // Cancel donation by deleting it
          await newsApi.deleteDonation(existingDonation.id)
          setModal({
            open: true,
            success: true,
            message: 'Your donation has been cancelled.',
            showUpdate: false,
            newAmount: null,
            overFive: false,
            isCancellation: false,
          })
          setExistingDonation(null)
          toast.success('Donation cancelled')
          return
        } else {
          res = await newsApi.updateDonation(existingDonation.id, { amount })
        }
      } else {
        res = await newsApi.createDonation({
          amount,
          feeder: feeder.alias,
          article: article.alias,
        })
      }

      setModal({
        open: true,
        success: true,
        message: existingDonation
          ? 'Your donation amount has been updated! 🎉'
          : '🎉 Thank you for your donation!',
        showUpdate: false,
        newAmount: null,
        overFive: false,
        isCancellation: false,
      })
      setExistingDonation(res.data)
      toast.success(existingDonation ? 'Donation updated!' : 'Thank you for donating!')
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Could not record donation!'
      setModal({
        open: true,
        success: false,
        message,
        showUpdate: false,
        newAmount: null,
        overFive: false,
        isCancellation: false,
      })
      toast.error(message)
    }
  }

  const handleUpdateConfirm = () => {
    donate(modal.newAmount)
  }

  if (!article) return null

  return (
    <>
      {/* Modal */}
      <AnimatePresence>
        {modal.open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center bg-black/40 z-50 p-4"
            onClick={() => setModal({ ...modal, open: false })}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 w-full max-w-md text-center ${modal.success === true
                ? 'border-l-4 border-green-500'
                : modal.success === false
                  ? 'border-l-4 border-red-500'
                  : 'border-l-4 border-yellow-500'
                }`}
            >
              <div className="mb-4">
                {modal.overFive ? (
                  <Info className="mx-auto w-12 h-12 text-primary" />
                ) : modal.success === true ? (
                  <CheckCircle2 className="mx-auto w-12 h-12 text-green-500" />
                ) : modal.success === false ? (
                  <XCircle className="mx-auto w-12 h-12 text-red-500" />
                ) : (
                  <AlertCircle className="mx-auto w-12 h-12 text-yellow-500" />
                )}
              </div>

              <div className="mb-6 text-lg text-gray-900 dark:text-gray-100">
                {modal.overFive ? (
                  <>
                    Please donate <span className="font-bold">${modal.amount}</span> directly to{' '}
                    <span className="font-semibold">{feeder.display_name || feeder.name}</span>'s official page.
                  </>
                ) : (
                  modal.message
                )}
              </div>

              {modal.overFive ? (
                <>
                  {feeder?.donation_link ? (
                    <>
                      <a
                        href={feeder.donation_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-logo-box
                        className="bg-primary hover:bg-primary/90 text-white font-medium px-6 py-2 rounded-full transition block w-full mb-4 transition-all duration-300"
                      >
                        Go to Donation Page
                      </a>
                      <div className="text-xs text-gray-400 dark:text-gray-500 break-all">
                        {feeder.donation_link}
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-gray-400 dark:text-gray-500 italic">
                      No donation page available.
                    </div>
                  )}
                  <Button
                    onClick={() => setModal({ ...modal, open: false })}
                    variant="outline"
                    className="mt-4"
                  >
                    Close
                  </Button>
                </>
              ) : modal.showUpdate ? (
                <div className="flex gap-4 justify-center">
                  {modal.isCancellation ? (
                    <>
                      <Button onClick={() => setModal({ ...modal, open: false })} variant="outline" className="flex-1">
                        Back
                      </Button>
                      <Button
                        onClick={handleUpdateConfirm}
                        className="flex-1 bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-slate-900 text-white"
                      >
                        Cancel Donation
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button onClick={handleUpdateConfirm} className="flex-1">
                        Update
                      </Button>
                      <Button onClick={() => setModal({ ...modal, open: false })} variant="outline" className="flex-1">
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              ) : (
                <Button onClick={() => setModal({ ...modal, open: false })}>Close</Button>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Donation Section */}
      <div className="bg-gradient-to-br from-primary/5 to-primary/10 dark:from-gray-800 dark:to-gray-900 rounded-2xl border border-primary/20 dark:border-gray-700 p-6 md:p-8 my-8 shadow-sm">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100">
              One-click Tip
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Your contribution helps keep independent journalism alive
            </p>
          </div>
        </div>

        {/* Existing Donation Badge */}
        {existingDonation && !isUpdating && (
          <div className="mb-4 inline-flex items-center gap-2 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-4 py-2 rounded-full text-sm font-medium">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            You've donated ${existingDonation.amount}
            <button
              onClick={() => setIsUpdating(true)}
              className="ml-2 text-green-700 dark:text-green-400 underline hover:no-underline"
            >
              Change
            </button>
            <button
              onClick={() => handleDonate(0)}
              className="ml-2 text-red-600 dark:text-red-400 underline hover:no-underline"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Update Form */}
        {isUpdating && (
          <div className="mb-6 bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Update your donation amount
            </label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1 max-w-xs">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="w-full pl-7 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <Button
                onClick={() => {
                  const amount = parseFloat(customAmount)
                  if (isNaN(amount) || amount < 0) {
                    toast.error('Please enter a valid amount (0 or greater).')
                    return
                  }
                  handleDonate(amount)
                  setIsUpdating(false)
                  setCustomAmount('')
                }}
              >
                Update
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIsUpdating(false)
                  setCustomAmount('')
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Quick Tip Buttons */}
        {!isUpdating && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-stretch gap-3">
              {amounts
                .filter(amt => amt > 0 || existingDonation) // Only show $0 if there's an existing donation
                .map((amt) => (
                  <button
                    key={amt}
                    onClick={() => handleDonate(amt === 0 ? 0 : (existingDonation ? parseFloat(existingDonation.amount) + amt : amt))}
                    className={`flex-1 ${amt === 0
                      ? 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 border-2 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'
                      : 'bg-white dark:bg-gray-800 hover:bg-primary/10 dark:hover:bg-gray-700 border-2 border-primary text-primary'
                      } rounded-xl px-6 py-4 font-semibold text-lg transition-all hover:scale-105 hover:shadow-md`}
                    type="button"
                  >
                    {amt === 0 ? 'No Tip' : `$${amt.toFixed(2)}`}
                  </button>
                ))}
            </div>

            {/* Give More Link */}
            {feeder?.donation_link && (
              <div className="pt-4 border-t border-primary/20 dark:border-gray-700">
                <button
                  onClick={() => window.open(feeder.donation_link, '_blank')}
                  data-logo-box
                  className="w-full bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-slate-900 text-white rounded-xl px-6 py-3 font-medium text-base transition-all hover:shadow-lg flex items-center justify-center gap-2 duration-300"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  Give More @ {feeder.display_name || feeder.name}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}
